import React, { useState } from "react";
import {
  TextField,
  Grid,
  IconButton,
  Chip,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
} from "@mui/material";
import { Plus, Trash2, Edit, Check, X } from "lucide-react";
import ProductSection from "../ProductSection";

// Sample products for related products dropdown
const SAMPLE_PRODUCTS = [
  { id: 1, name: "Anarkali Style Salwar Suit", code: "SS-001" },
  { id: 2, name: "Designer Palazzo Suit", code: "SS-003" },
  { id: 3, name: "Banarasi Jacquard Suit", code: "SS-005" },
  { id: 4, name: "Sharara Style Suit", code: "SS-008" },
  { id: 5, name: "Casual Wear Cotton Suit	", code: "SS-009" },
];

const AdditionalDetails = ({ productData, updateProductData, errors }) => {
  const [showAddAttribute, setShowAddAttribute] = useState(false);
  const [attributeData, setAttributeData] = useState({
    name: "",
    value: "",
  });
  const [editingAttributeIndex, setEditingAttributeIndex] = useState(null);
  const [newIdentifier, setNewIdentifier] = useState("");
  const [editingIdentifierIndex, setEditingIdentifierIndex] = useState(null);
  const [editingIdentifier, setEditingIdentifier] = useState("");

  // Handle adding a new attribute
  const handleAddAttribute = () => {
    if (editingAttributeIndex !== null) {
      // Update existing attribute
      const updatedAttributes = [...productData.attributes];
      updatedAttributes[editingAttributeIndex] = attributeData;
      updateProductData({ attributes: updatedAttributes });
      setEditingAttributeIndex(null);
    } else {
      // Add new attribute
      updateProductData({
        attributes: [...productData.attributes, attributeData],
      });
    }

    // Reset form
    setAttributeData({
      name: "",
      value: "",
    });
    setShowAddAttribute(false);
  };

  // Handle editing an attribute
  const handleEditAttribute = index => {
    setAttributeData(productData.attributes[index]);
    setEditingAttributeIndex(index);
    setShowAddAttribute(true);
  };

  // Handle deleting an attribute
  const handleDeleteAttribute = index => {
    const updatedAttributes = [...productData.attributes];
    updatedAttributes.splice(index, 1);
    updateProductData({ attributes: updatedAttributes });
  };

  // Handle adding a new identifier
  const handleAddIdentifier = e => {
    if (e.key === "Enter" && newIdentifier.trim()) {
      updateProductData({
        alternateIdentifiers: [
          ...productData.alternateIdentifiers,
          newIdentifier.trim(),
        ],
      });
      setNewIdentifier("");
    }
  };

  // Handle editing an identifier
  const handleStartEditIdentifier = index => {
    setEditingIdentifierIndex(index);
    setEditingIdentifier(productData.alternateIdentifiers[index]);
  };

  // Save edited identifier
  const handleSaveEditIdentifier = () => {
    if (editingIdentifier.trim()) {
      const updatedIdentifiers = [...productData.alternateIdentifiers];
      updatedIdentifiers[editingIdentifierIndex] = editingIdentifier.trim();
      updateProductData({ alternateIdentifiers: updatedIdentifiers });
    }
    setEditingIdentifierIndex(null);
  };

  // Cancel editing identifier
  const handleCancelEditIdentifier = () => {
    setEditingIdentifierIndex(null);
  };

  // Handle deleting an identifier
  const handleDeleteIdentifier = index => {
    const updatedIdentifiers = [...productData.alternateIdentifiers];
    updatedIdentifiers.splice(index, 1);
    updateProductData({ alternateIdentifiers: updatedIdentifiers });
  };

  // Handle adding a related product
  const handleAddRelatedProduct = (_, value) => {
    if (!value) return;

    // Check if product is already in the list
    if (productData.relatedProducts.some(p => p.id === value.id)) return;

    updateProductData({
      relatedProducts: [...productData.relatedProducts, value],
    });
  };

  // Handle removing a related product
  const handleRemoveRelatedProduct = id => {
    const updatedRelatedProducts = productData.relatedProducts.filter(
      p => p.id !== id,
    );
    updateProductData({ relatedProducts: updatedRelatedProducts });
  };

  // Handle notes change
  const handleNotesChange = e => {
    updateProductData({ notes: e.target.value });
  };

  return (
    <div className="space-y-6">
      <ProductSection data="Additional Identifiers" />

      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {productData?.alternateIdentifiers?.map((identifier, index) => (
            <div key={index} className="flex items-center">
              {editingIdentifierIndex === index ? (
                <div className="flex items-center border rounded-md overflow-hidden">
                  <TextField
                    value={editingIdentifier}
                    onChange={e => setEditingIdentifier(e.target.value)}
                    size="small"
                    variant="outlined"
                    sx={{ width: "150px" }}
                    autoFocus
                  />
                  <IconButton
                    size="small"
                    onClick={handleSaveEditIdentifier}
                    color="primary">
                    <Check size={16} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancelEditIdentifier}
                    color="error">
                    <X size={16} />
                  </IconButton>
                </div>
              ) : (
                <Chip
                  label={identifier}
                  onDelete={() => handleDeleteIdentifier(index)}
                  onClick={() => handleStartEditIdentifier(index)}
                  sx={{
                    fontFamily: "Outfit",
                    "& .MuiChip-deleteIcon": {
                      color: "#d32f2f",
                    },
                  }}
                />
              )}
            </div>
          ))}
        </div>

        <TextField
          placeholder="Add identifier and press Enter"
          value={newIdentifier}
          onChange={e => setNewIdentifier(e.target.value)}
          onKeyDown={handleAddIdentifier}
          size="small"
          fullWidth
          variant="outlined"
          InputProps={{
            sx: {
              borderRadius: "0.5rem",
              fontFamily: "Outfit",
              fontSize: "0.875rem",
            },
            startAdornment: (
              <InputAdornment position="start">
                <Plus size={16} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <ProductSection data="Product Attributes" />

      <div className="border border-gray-200 rounded-lg p-4">
        {productData.attributes.length === 0 && !showAddAttribute ? (
          <div className="text-center py-4">
            <p className="text-gray-500 mb-2">No attributes added yet</p>
            <button
              onClick={() => setShowAddAttribute(true)}
              className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#F9F8FE] flex items-center gap-1 mx-auto">
              <Plus size={14} />
              Add Attribute
            </button>
          </div>
        ) : (
          <>
            {productData.attributes.length > 0 && (
              <TableContainer
                component={Paper}
                sx={{ borderRadius: "0.5rem", mb: 2 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                        }}>
                        Attribute
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                        }}>
                        Value
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          width: "100px",
                        }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productData?.attributes?.map((attr, index) => (
                      <TableRow key={index}>
                        <TableCell
                          sx={{ fontFamily: "Outfit", fontSize: "0.875rem" }}>
                          {attr.name}
                        </TableCell>
                        <TableCell
                          sx={{ fontFamily: "Outfit", fontSize: "0.875rem" }}>
                          {attr.value}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <IconButton
                              size="small"
                              onClick={() => handleEditAttribute(index)}
                              sx={{ color: "#5652B0", padding: "4px" }}>
                              <Edit size={14} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAttribute(index)}
                              sx={{ color: "#d32f2f", padding: "4px" }}>
                              <Trash2 size={14} />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {showAddAttribute && (
                      <TableRow>
                        <TableCell>
                          <TextField
                            placeholder="Attribute name"
                            value={attributeData.name}
                            onChange={e =>
                              setAttributeData({
                                ...attributeData,
                                name: e.target.value,
                              })
                            }
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              sx: {
                                borderRadius: "0.5rem",
                                fontFamily: "Outfit",
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="Attribute value"
                            value={attributeData.value}
                            onChange={e =>
                              setAttributeData({
                                ...attributeData,
                                value: e.target.value,
                              })
                            }
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              sx: {
                                borderRadius: "0.5rem",
                                fontFamily: "Outfit",
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <IconButton
                              size="small"
                              onClick={handleAddAttribute}
                              disabled={
                                !attributeData.name || !attributeData.value
                              }
                              sx={{
                                color: "#5652B0",
                                padding: "4px",
                                "&.Mui-disabled": {
                                  color: "rgba(0, 0, 0, 0.26)",
                                },
                              }}>
                              <Check size={14} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setShowAddAttribute(false);
                                setEditingAttributeIndex(null);
                                setAttributeData({ name: "", value: "" });
                              }}
                              sx={{ color: "#d32f2f", padding: "4px" }}>
                              <X size={14} />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!showAddAttribute && (
              <button
                onClick={() => {
                  setShowAddAttribute(true);
                  setEditingAttributeIndex(null);
                  setAttributeData({ name: "", value: "" });
                }}
                className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-sm font-medium hover:bg-[#F9F8FE] flex items-center gap-1">
                <Plus size={14} />
                Add Attribute
              </button>
            )}
          </>
        )}
      </div>

      <ProductSection data="Related Products" />

      <div className="border border-gray-200 rounded-lg p-4">
        <Autocomplete
          options={SAMPLE_PRODUCTS.filter(
            p => !productData?.relatedProducts?.some(rp => rp.id === p.id),
          )}
          getOptionLabel={option => `${option.name} (${option.code})`}
          onChange={handleAddRelatedProduct}
          size="small"
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Search and select related products"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                sx: {
                  borderRadius: "0.5rem",
                  fontFamily: "Outfit",
                  fontSize: "0.875rem",
                },
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        <div className="flex flex-wrap gap-2">
          {productData?.relatedProducts?.map(product => (
            <Chip
              key={product.id}
              label={`${product.name} (${product.code})`}
              onDelete={() => handleRemoveRelatedProduct(product.id)}
              sx={{
                fontFamily: "Outfit",
                "& .MuiChip-deleteIcon": {
                  color: "#d32f2f",
                },
              }}
            />
          ))}
        </div>
      </div>

      <ProductSection data="Notes" />

      <div className="border border-gray-200 rounded-lg p-4">
        <TextField
          placeholder="Add any additional notes about this product"
          value={productData.notes}
          onChange={handleNotesChange}
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          InputProps={{
            sx: {
              borderRadius: "0.5rem",
              fontFamily: "Outfit",
              fontSize: "0.875rem",
            },
          }}
        />
      </div>
    </div>
  );
};

export default AdditionalDetails;
